import type { MonitorStatus } from '@repo/api-codegen';
import {
	useApiGetMonitorPossibleStaled,
	useApiGetResourcePossibleArchived,
	useGetPersona,
} from '@repo/api-codegen';
import { createMockableHook } from '@repo/common/utils/createMockableHook';
import { isPersonaUrl } from '@repo/common/utils/urls';
import { useEffect, useState } from 'react';
import { useUser } from '../../api';
import type {
	AIPersonaPreview,
	DefaultAIPersonaPreview,
	EntityPreview,
	MonitorPreview,
	UserPreview,
} from '../ResourcePreview/ResourcePreview.types';

const ID_REGEX =
	/\/([^ ]*)\/([^ ]*)?([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/;

export enum ResourceLinkType {
	Entity = 'entity',
	User = 'user',
	Monitor = 'monitor',
	AIChat = 'ai_chat',
	AIPersona = 'ai_persona',
	DefaultAIPersona = 'default_ai_persona',
}

function useResourceLinkInternal({
	href,
}: {
	href: string | undefined | null;
}) {
	const [id, setId] = useState<string>('');
	const [resourceType, setResourceType] = useState<ResourceLinkType | null>(
		null
	);

	const {
		data: personaData,
		isLoading: isLoadingPersona,
		error: errorPersona,
	} = useGetPersona(
		{
			pathParams: { personaId: id },
		},
		{
			enabled: resourceType === ResourceLinkType.AIPersona,
		}
	);

	const {
		data: userData,
		isLoading: isLoadingUser,
		error: errorUser,
	} = useUser({
		id,
		options: {
			enabled: resourceType === ResourceLinkType.User,
		},
	});

	const {
		data: monitorData,
		isLoading: isLoadingMonitor,
		error: errorMonitor,
	} = useApiGetMonitorPossibleStaled(
		{
			pathParams: {
				monitorId: id,
			},
		},
		{
			enabled: resourceType === ResourceLinkType.Monitor,
		}
	);

	const {
		data: entityData,
		isLoading: isLoadingEntity,
		error: errorEntity,
	} = useApiGetResourcePossibleArchived(
		{
			pathParams: {
				entityId: id,
			},
		},
		{
			enabled: resourceType === ResourceLinkType.Entity,
		}
	);

	useEffect(() => {
		if (!href) {
			return;
		}

		if (isPersonaUrl(href)) {
			if (href === '/ai/new') {
				setResourceType(ResourceLinkType.DefaultAIPersona);
				setId('default-persona');
				return;
			}

			setResourceType(ResourceLinkType.AIPersona);
			setId(href.split('persona=')[1]);
			return;
		}

		const matches = href.match(ID_REGEX);

		if (matches?.length !== 4) {
			return;
		} else if (matches?.[1].endsWith('user')) {
			setResourceType(ResourceLinkType.User);
			setId(matches?.[3]);
		} else if (matches?.[1].endsWith('monitor')) {
			setResourceType(ResourceLinkType.Monitor);
			setId(matches?.[3]);
		} else if (matches?.[1].endsWith('ai')) {
			setResourceType(ResourceLinkType.AIChat);
			setId(matches?.[3]);
		} else {
			setResourceType(ResourceLinkType.Entity);
			setId(matches?.[3]);
		}
	}, [href]);

	let data:
		| UserPreview
		| MonitorPreview
		| EntityPreview
		| AIPersonaPreview
		| DefaultAIPersonaPreview
		| undefined = entityData as EntityPreview | undefined;
	if (resourceType === ResourceLinkType.User) {
		data = userData as UserPreview | undefined;
	} else if (resourceType === ResourceLinkType.AIPersona) {
		data = {
			id: personaData?.id,
			entity_type: 'ai_persona',
			persona: personaData,
		} as AIPersonaPreview | undefined;
	} else if (resourceType === ResourceLinkType.DefaultAIPersona) {
		data = {
			id: 'default-persona',
			entity_type: 'ai_persona',
			isDefaultAiPersona: true,
		} as DefaultAIPersonaPreview | undefined;
	} else if (resourceType === ResourceLinkType.Monitor) {
		data = {
			id: monitorData?.id,
			entity_type: 'monitor',
			title: monitorData?.name,
			description: monitorData?.description,
			status: monitorData?.status as MonitorStatus,
			stale: !!monitorData?.staled_at,
			owners: monitorData?.owner_users ?? [],
		} as MonitorPreview | undefined;
	}

	return {
		id,
		data,
		resourceType,
		isLoading:
			isLoadingEntity || isLoadingUser || isLoadingPersona || isLoadingMonitor,
		hasError: !!errorEntity || !!errorUser || !!errorPersona || !!errorMonitor,
	};
}

export const [useResourceLink, MockUseResourceLink] = createMockableHook(
	useResourceLinkInternal
);
